import { useEffect, useState } from "react";
import { getStatus, type StatusRow } from "./actions";
import { downloadCsv } from "./csv";

// Status: did each periodic thing the console runs succeed or fail? Each operation gets a strip of days (same look as the DLA BUS strips):
//   green  complete     red  failed     green with a red edge  some runs failed     hollow red  missing weekday     hollow amber  missing weekend
//   grey   nothing to do that day (an operation that only runs when something changed is never "missing")
// Data fetches and image fetches are separate, inventory is separate from the catalog. Built from the free logs the console keeps itself.
// Things that are not connected yet are listed at the bottom so they have a place waiting.

const when = (s: string) => (s ? s.slice(0, 16).replace("T", " ") : "");
const csvCell = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const TITLE: Record<string, string> = {
  ok: "complete", failed: "failed", partial: "some runs failed", none: "nothing to do", missing_business: "missing (weekday)", missing_weekend: "missing (weekend)",
};

function StripBlock({ r }: { r: StatusRow }) {
  const s = r.strip!;
  const gap = s.missing.length === 0
    ? <span className="strip-badge ok">no gap</span>
    : <span className="strip-badge bad">{s.missing.length} missing · {s.missingBusiness} business</span>;
  const failed = s.days.filter((d) => d.state === "failed" || d.state === "partial").length;
  return (
    <div className="strip-block">
      <div className="strip-head">
        <b>{r.name}</b>
        <span className="strip-range">{s.from}..{s.to}</span>
        {gap}
        {failed > 0 && <span className="strip-badge warn">{failed} day{failed === 1 ? "" : "s"} with a failure</span>}
        <span className="strip-count">{s.covered} of {s.expected} dates</span>
      </div>
      <div className="strip" role="img" aria-label={`${r.name}: ${s.covered} of ${s.expected} dates`}>
        {s.days.map((d) => (
          <span key={d.date} className={`sq sq-${d.state}`} title={`${d.date} · ${TITLE[d.state]}${d.ok || d.failed ? ` · ${d.ok} ok, ${d.failed} failed` : ""}`} />
        ))}
      </div>
      <div className="strip-legend">
        <span><i className="sq sq-ok" /> complete</span>
        {s.days.some((d) => d.state === "failed" || d.state === "partial") && <span><i className="sq sq-failed" /> failed</span>}
        {s.days.some((d) => d.state === "none") && <span><i className="sq sq-none" /> nothing to do</span>}
        {s.missing.length > 0 && <span><i className="sq sq-missing_business" /> missing</span>}
        <span className="meta" style={{ margin: 0 }}>
          {r.lastOk ? `last success ${when(r.lastOk)}` : "no success yet"}{r.lastFail ? ` · last failure ${when(r.lastFail)}` : ""}{r.detail ? ` · ${r.detail}` : ""}
        </span>
      </div>
      {s.missing.length > 0 && (
        <div className="strip-chips">
          {s.missing.map((m) => <span key={m.date} className={`chip ${m.weekend ? "weekend" : "business"}`}>{m.date} {m.weekday}</span>)}
        </div>
      )}
    </div>
  );
}

export function StatusPanel() {
  const [rows, setRows] = useState<StatusRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    const r = await getStatus();
    setLoading(false);
    if (!r.ok) { setError(r.error); return; }
    setError(null); setRows(r.data.rows);
  };
  useEffect(() => { load(); }, []);
  const withStrip = rows.filter((r) => r.strip && r.strip.days.length > 0);
  const others = rows.filter((r) => !(r.strip && r.strip.days.length > 0));
  const groups = Array.from(new Set(withStrip.map((r) => r.group)));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1>Status</h1>
          <p className="meta">
            One strip per periodic operation, one square per day. Operations that must run every day show missing days; ones that only run when something changed
            show quiet days in grey. Hover a square for the day.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>
          <button type="button" className="sheet-export" disabled={!rows.length}
                  onClick={() => downloadCsv("hec-status.csv", [["group", "what", "status", "last success", "last failure", "dates covered", "dates expected", "missing dates", "detail"],
                    ...rows.map((r) => [r.group, r.name, r.status, r.lastOk, r.lastFail, r.strip?.covered ?? "", r.strip?.expected ?? "", (r.strip?.missing ?? []).map((m) => m.date).join(" "), r.detail])]
                    .map((l) => l.map(csvCell).join(",")).join("\r\n"))}>Export status ({rows.length})</button>
        </div>
      </div>
      {error && <div className="panel error">{error}</div>}

      {groups.map((g) => (
        <section key={g}>
          <h3 style={{ margin: "22px 0 4px" }}>{g}</h3>
          {withStrip.filter((r) => r.group === g).map((r) => <StripBlock key={r.name} r={r} />)}
        </section>
      ))}

      <h3 style={{ margin: "26px 0 4px" }}>Other</h3>
      <table className="manual-products">
        <thead><tr><th>Status</th><th>What</th><th>Last success</th><th>Last failure</th><th>Detail</th></tr></thead>
        <tbody>
          {others.map((r) => (
            <tr key={r.group + r.name}>
              <td><span className={`dot dot-${r.status === "bad" ? "red" : r.status === "warn" ? "orange" : r.status === "ok" ? "ok" : "none"}`} role="img" aria-label={r.status} /></td>
              <td className="wrap">{r.name}</td><td className="mono">{when(r.lastOk) || "—"}</td><td className="mono">{when(r.lastFail) || "—"}</td>
              <td className="wrap" style={{ whiteSpace: "normal" }}>{r.detail || (r.op ? "no history recorded yet: the strip starts with the next run" : "")}</td>
            </tr>
          ))}
          {others.length === 0 && !loading && <tr><td colSpan={5} className="meta">Nothing else.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
